import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Building2, TrendingUp, DollarSign, Users } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('superadmin_token');
    if (!token) {
      navigate('/orderlab-admin/login');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/superadmin/stats/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401) {
        navigate('/orderlab-admin/login');
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, subtitle, trend }) => (
    <Card className="p-6 hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="p-3 rounded-lg bg-primary/10">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-xs font-medium text-green-600">
            <TrendingUp className="w-3 h-3" />
            {trend}
          </div>
        )}
      </div>
      <div>
        <p className="text-sm text-muted-foreground mb-1">{label}</p>
        <p className="text-3xl font-semibold tabular-nums">{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-2">{subtitle}</p>}
      </div>
    </Card>
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-semibold mb-2">Dashboard</h1>
        <p className="text-muted-foreground">Vue d'ensemble de la plateforme ORDER LAB</p>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Building2}
          label="Total Restaurants"
          value={stats?.total_restaurants || 0}
          subtitle={`${stats?.restaurants_this_month || 0} ce mois`}
          trend={stats?.restaurants_this_month > 0 ? `+${stats.restaurants_this_month}` : null}
        />
        <StatCard
          icon={Users}
          label="Restaurants Actifs"
          value={stats?.active_restaurants || 0}
          subtitle="< 30 jours"
        />
        <StatCard
          icon={DollarSign}
          label="MRR"
          value={`${(stats?.total_mrr || 0).toFixed(2)}€`}
          subtitle="Revenu mensuel récurrent"
        />
        <StatCard
          icon={TrendingUp}
          label="Taux de Conversion"
          value={`${stats?.conversion_rate || 0}%`}
          subtitle="Signup → 1ère commande"
        />
      </div>

      {/* Plans Breakdown */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Répartition par Plan</h2>
        <div className="grid grid-cols-3 gap-4">
          {['Free', 'Pro', 'Plus'].map((plan) => {
            const count = stats?.[`${plan.toLowerCase()}_count`] || 0;
            return (
              <div key={plan} className="p-4 rounded-lg border bg-muted/30">
                <p className="text-sm text-muted-foreground mb-1">{plan}</p>
                <p className="text-2xl font-semibold tabular-nums">{count}</p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Chart Placeholder */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Inscriptions (30 derniers jours)</h2>
        <div className="h-64 flex items-center justify-center border border-dashed rounded-lg">
          <p className="text-sm text-muted-foreground">Graphique à venir (Recharts)</p>
        </div>
        <p className="text-sm text-muted-foreground mt-4">Total inscriptions: {stats?.signups?.length || 0}</p>
      </Card>
    </div>
  );
}
